/**
 * Every event this app may send, and what may travel with it (GIFTWEB-33).
 *
 * NAMES ARE PROVISIONAL. `miawealth-app/docs/ANALYTICS_EVENTS.md` governs them
 * and is not available yet; when it arrives the names change here and nowhere
 * else, which is the reason for the indirection. Call sites use the keys.
 *
 * Properties are deliberately coarse. An analytics report answers "how many
 * people got this far", and every extra dimension is another way to single
 * somebody out — a £73 gift at 14:02 is one person, a "£51 to £100" gift at
 * 14:02 is not.
 */

import type { InfoSheetKey } from '@/content/info-sheets';

export const ANALYTICS_EVENTS = {
  giftStarted: 'Gift started',
  amountSubmitted: 'Amount submitted',
  detailsSubmitted: 'Details submitted',
  messageSubmitted: 'Message submitted',
  messageSkipped: 'Message skipped',
  paymentStarted: 'Payment started',
  paymentSucceeded: 'Payment succeeded',
  paymentNotCompleted: 'Payment not completed',
  appStoreOpened: 'App store opened',
  supportOpened: 'Support opened',
  infoSheetOpened: 'Information sheet opened',
} as const;

export type AnalyticsEventKey = keyof typeof ANALYTICS_EVENTS;

/** Gift sizes, as bands. The exact figure is never sent. */
export type AmountBand = '10-25' | '26-50' | '51-100' | '101-250' | '250+';

export function amountBand(amount: number): AmountBand {
  if (amount <= 25) return '10-25';
  if (amount <= 50) return '26-50';
  if (amount <= 100) return '51-100';
  if (amount <= 250) return '101-250';
  return '250+';
}

export type MessageKind = 'text' | 'audio' | 'video' | 'photo';

/**
 * What each event may carry. `never` means the event takes no properties —
 * most of the funnel is a count, not a description.
 */
export type AnalyticsEventProps = {
  giftStarted: never;
  amountSubmitted: { band: AmountBand; privateAmount: 'yes' | 'no' };
  detailsSubmitted: never;
  messageSubmitted: { kind: MessageKind };
  messageSkipped: never;
  paymentStarted: never;
  paymentSucceeded: never;
  /** `outcome` is the open-banking status, e.g. failed, cancelled, expired. */
  paymentNotCompleted: { outcome: string };
  /** Which store they went to, not the platform we guessed. */
  appStoreOpened: { store: 'ios' | 'android' };
  /** Which affordance the contributor used to reach support. */
  supportOpened: { from: 'pill' | 'tile' | 'allowance' };
  infoSheetOpened: { sheet: InfoSheetKey };
};
